import { Suspense, lazy } from 'react';

import { useWorkflow } from '../hooks/useWorkflow';
import { WorkflowSkeleton } from './WorkflowSkeleton';

/**
 * A workflow's shape, and nothing else (spec 094 FR-010).
 *
 * **Not `WorkflowGraphPage`.** That is a *page*: it carries the workflow's name,
 * its key, its step count, an Edit link and its description, because it is the
 * whole of what a reader sees at that address. Beside a board, every one of those
 * is already on screen — the board's own header says the name and the description
 * sits directly above it — so rendering the page duplicated both and put a second
 * Edit control a few pixels from the first.
 *
 * It also could not fill its container. The page's root is `flex-1`, which needs a
 * flex parent with a definite height to resolve against; dropped into a plain
 * fixed-height box it collapsed to nothing and drew an empty frame.
 *
 * So this is the canvas, sized to whatever it is given. It owns its own lazy
 * boundary for the same reason the page does: React Flow is heavy, constitution
 * §6 requires it behind one, and a boundary here does not care how the route above
 * happens to be split.
 *
 * **Read-only and unselectable by design.** The page's selection lives in *its*
 * search params, and a second selectable graph would either need its own
 * parameters on the board's address or would silently write the page's. Beside a
 * board the diagram answers "what is the shape of this" — the address for
 * inspecting a step already exists, and the Edit control is right there.
 */
const WorkflowGraph = lazy(() =>
  import('./WorkflowGraph').then((module) => ({
    default: module.WorkflowGraph,
  })),
);

export interface WorkflowDiagramProps {
  workspaceId: string;
  workflowKey: string;
}

export function WorkflowDiagram({
  workspaceId,
  workflowKey,
}: WorkflowDiagramProps) {
  const { workflow, isPending, loadFailed } = useWorkflow(
    workspaceId,
    workflowKey,
  );

  if (isPending) {
    return <WorkflowSkeleton label="Loading diagram" />;
  }

  // Said, not drawn as an empty canvas. A blank frame where a diagram was asked
  // for reads as a broken feature rather than as a failed read — and the board
  // beside it is unaffected, so the reader should be told which half is missing.
  if (loadFailed || !workflow) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center">
        <p className="text-muted-foreground text-sm">
          The diagram could not be loaded. The board below is unaffected.
        </p>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full p-2">
      <Suspense fallback={<WorkflowSkeleton label="Loading diagram" />}>
        <WorkflowGraph
          workspaceId={workspaceId}
          workflow={workflow}
          selection={null}
          onSelect={() => undefined}
          onClearSelection={() => undefined}
        />
      </Suspense>
    </div>
  );
}
